/**
 * Outer layer of the review deck. The current card is the caller's content.
 * At most two empty glass surfaces sit behind it. Geometry, colour, blur,
 * and reduced motion live on the revisao-stack / revisao-glow classes.
 */
import type { ReactNode } from "react";
import { GlassCard } from "./GlassCard";

export interface CardStackCard {
  id: string;
  /** Mounted only for the current card (index 0). */
  content: ReactNode;
}

export interface CardStackProps {
  /** Queue, current card first. May be long (40). */
  cards: readonly CardStackCard[];
  className?: string;
}

export function CardStack({ cards, className }: CardStackProps) {
  if (cards.length === 0) return null;

  const backCount = Math.min(2, cards.length - 1);
  const joined = ["revisao-card-stack", className].filter(Boolean).join(" ");

  return (
    <div className={joined} data-stack-count={cards.length}>
      <div className="revisao-stack-stage">
        <div
          className="revisao-glow revisao-glow-green"
          data-glow="green"
          aria-hidden={true}
        />
        <div
          className="revisao-glow revisao-glow-second"
          data-glow="second"
          aria-hidden={true}
        />
        {backCount >= 2 ? (
          <div
            className="revisao-stack-layer revisao-stack-back revisao-stack-back-far"
            data-stack-role="back"
            data-stack-depth={2}
            aria-hidden={true}
          >
            <GlassCard className="h-full w-full" />
          </div>
        ) : null}
        {backCount >= 1 ? (
          <div
            className="revisao-stack-layer revisao-stack-back revisao-stack-back-near"
            data-stack-role="back"
            data-stack-depth={1}
            aria-hidden={true}
          >
            <GlassCard className="h-full w-full" />
          </div>
        ) : null}
        <div
          className="revisao-stack-layer revisao-stack-current"
          data-stack-role="current"
          key={cards[0].id}
        >
          {cards[0].content}
        </div>
      </div>
    </div>
  );
}
